import { type MouseEvent, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { ErrorBoundary } from 'shared/util/ErrorBoundary';
import { trackBasicPageView } from 'shared/search/common/tracking';
import { updateBannerSubverticalValues } from 'shared/banners/bannerParams';
import { googleBreadcrumb } from './util/googleBreadcrumb';
import { googleFaq } from './util/googleFaq';
import { Hotjar } from 'shared/util/hotjar';

import { Head } from 'shared/page/warp/Head';
import { Main } from 'shared/page/warp/Main';
import { FrontpageSeo } from 'shared/seo/warp/FrontpageSeo';
import { OtherVerticalsNav } from '@/components/otherVerticalsNav/OtherVerticalsNav';
import { CarRentalSale } from '@/components/careRentalSale/CarRentalSale';
import { FrontpageBanner } from '@/components/frontpageBanner/FrontpageBanner';
import { CheapestDestinations } from '@/components/cheapestDestinations/CheapestDestinations';

import { Menu } from 'shared/page/warp/Menu';
import { HeroBanner } from 'shared/page/warp/HeroBanner';
import { FlightSearch } from 'shared/search/warp/flight/FlightSearch';
import { HotelSearch } from 'shared/search/warp/hotel/HotelSearch';
import { CarSearch } from 'shared/search/warp/car/CarSearch';
import { PrePackageSearch } from 'shared/search/warp/prepackage/PrePackageSearch';
import { FhhSearch } from 'shared/search/warp/fhh/FhhSearch';

import { ConfigProvider } from 'shared/config/ConfigContext';
import { DeviceTypeProvider } from 'shared/contexts/DeviceTypeProvider';
import { t } from 'shared/locale/texts';

import type { Vertical } from 'shared/search/common/config';
import { SearchVerticalConfig } from 'shared/search/common/config';
import type { feConfig } from '@/config';
import type { SspProps } from '@/util/verticalSsp';
import type { VerticalConfigProps } from '@/config/verticalConfig';
import { verticalConfig } from '@/config/verticalConfig';

import styles from './Frontpage.module.css';

type Props = {
    config: VerticalConfigProps;
    feConfig: typeof feConfig;
} & SspProps;

export const Frontpage = ({ config, feConfig, cmsPodletContent, unleash, hasLegalBasis, cheapestDestinations, deviceType }: Props) => {
    const router = useRouter();
    const [activeVertical, setActiveVertical] = useState<Vertical>(config.vertical);
    const activeVerticalConfig = verticalConfig[activeVertical];

    useEffect(() => {
        const trackRouteChange = () => {
            trackBasicPageView(config.vertical);
        };

        router.events.on('routeChangeComplete', trackRouteChange);
        return () => {
            router.events.off('routeChangeComplete', trackRouteChange);
        };
    }, [router, config.vertical]);

    useEffect(() => {
        trackBasicPageView(activeVertical);
    }, [activeVertical]);

    const pushUrlPath = (_vertical: Vertical, fullPath: string) =>
        router.push(fullPath.replace('/reise', ''), undefined, { shallow: true });

    const setVertical = (event: MouseEvent<HTMLButtonElement>, clickedVertical: Vertical): void => {
        event.preventDefault();
        setActiveVertical(clickedVertical);
        updateBannerSubverticalValues(clickedVertical);
        pushUrlPath(clickedVertical, SearchVerticalConfig[clickedVertical].fullPath);
    };

    const hotjarEnabled = !!unleash?.hotjar && !!hasLegalBasis;
    const cdLength = cheapestDestinations?.length;

    const showCheapestDestinations = () => activeVertical === 'flight' && cdLength != null && cdLength > 0;

    return (
        <ErrorBoundary key={`${config.vertical}SearchErrorBoundary`}>
            <ConfigProvider config={feConfig}>
                <DeviceTypeProvider deviceType={deviceType}>
                    <Head
                        title={t(`htmlMetadata.${activeVertical}.title`)}
                        description={t(`htmlMetadata.${activeVertical}.description`)}
                        canonical={activeVerticalConfig.canonical}>
                        {googleBreadcrumb(activeVertical)}
                        {googleFaq(activeVertical)}
                    </Head>
                    <Main
                        className={styles['frontpage-container']}
                        customContainer
                        broadcastUrl={`https://www.finn.no/broadcasts?path=https://www.finn.no/reise/fp/${activeVertical}`}>
                        <div className={styles['frontpage-menu-wrapper']}>
                            <Menu activeVertical={activeVertical} setVertical={setVertical} unleash={unleash} />
                        </div>
                        <HeroBanner vertical={activeVertical}>
                            <VerticalSwitch vertical={activeVertical} unleash={unleash} />
                        </HeroBanner>
                        <div className={styles['frontpage-wrapper']}>
                            <h1 className="sr-only" data-testid="travelHeader">
                                {t('travel')}
                            </h1>
                            <div className="mt-16 md:mt-32">
                                {/*@ts-ignore this custom element is provided by the advertising podlet on runtime */}
                                <advt-component placementId="advt_middle_1" />
                            </div>
                            {showCheapestDestinations() ? (
                                <CheapestDestinations destinations={cheapestDestinations} />
                            ) : (
                                <FrontpageBanner />
                            )}
                            <OtherVerticalsNav />
                            {unleash?.carRentalSale ? <CarRentalSale /> : null}
                            {cmsPodletContent && <section data-testid="cms" dangerouslySetInnerHTML={{ __html: cmsPodletContent }} />}
                            <FrontpageSeo vertical={activeVertical} />
                        </div>
                    </Main>
                    <Hotjar enabled={hotjarEnabled} />
                </DeviceTypeProvider>
            </ConfigProvider>
        </ErrorBoundary>
    );
};

type VerticalSwitchProps = {
    vertical: Vertical;
    unleash: Record<string, boolean> | null; // cannot be undefined as that is not serializable (toggles are returned from getSSP)
};

const VerticalSwitch = ({ vertical, unleash }: VerticalSwitchProps) => {
    switch (vertical) {
        case 'flight':
            return <FlightSearch position="travel frontpage" />;
        case 'hotel':
            return <HotelSearch position="travel frontpage" />;
        case 'carrental':
            return <CarSearch unleash={unleash} position="travel frontpage" />;
        case 'prepackage':
            return <PrePackageSearch unleash={unleash} position="travel frontpage" />;
        case 'fhh':
            return <FhhSearch position="travel frontpage" />;
        default:
            return null;
    }
};
